import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, Outlet, Link } from "react-router-dom";
import { useAuth } from "../auth";
import { LogoLight } from "../components/Logo";
import { RUBRIQUES } from "../rubriques";
import api from "../api";
import UpsellBanner from "../components/UpsellBanner";
import OnboardingWizard from "../components/OnboardingWizard";
import {
  User, Users, Heart, FileText, Home, TrendingUp, AlertCircle, Feather,
  Cloud, Shield, UserCheck, LogOut, Sparkles, Archive, Share2, Lock, CreditCard, Menu, X, Gift, Briefcase, Handshake,
} from "lucide-react";

const ICONS = { User, Users, Heart, FileText, Home, TrendingUp, AlertCircle, Feather, Cloud, Shield, UserCheck };

function FoyerBadge() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/couple/me")
      .then((r) => { if (r.data.status === "active") setData(r.data); })
      .catch(() => {});
  }, []);
  if (!data) return null;
  return (
    <Link
      to="/securite"
      style={{ display: "block", margin: "12px 16px 6px", padding: "12px 14px", background: "rgba(202,168,106,0.12)", border: "1px solid rgba(202,168,106,0.3)", borderRadius: 6, textDecoration: "none" }}
      data-testid="sidebar-foyer-badge"
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <Heart size={14} style={{ color: "var(--auprea-gold)" }} />
        <span style={{ fontSize: 10, letterSpacing: "0.15em", color: "rgba(243,239,230,0.85)", textTransform: "uppercase", fontWeight: 600 }}>Carnet du foyer</span>
      </div>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 6 }}>
        <span style={{ fontSize: 11, color: "rgba(243,239,230,0.65)" }}>avec {data.partner.full_name?.split(" ")[0] || "votre conjoint"}</span>
        <span style={{ fontFamily: "Spectral, serif", fontSize: 18, color: "var(--auprea-gold)", fontWeight: 500 }}>{data.foyer_progression}%</span>
      </div>
      <div style={{ height: 4, background: "rgba(21,41,79,0.4)", borderRadius: 999, overflow: "hidden" }}>
        <div style={{ width: `${data.foyer_progression}%`, height: "100%", background: "linear-gradient(90deg, var(--auprea-gold), #e0c280)", transition: "width 0.6s ease" }} />
      </div>
    </Link>
  );
}

export default function DashboardLayout() {
  const { user, logout, refresh } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [dobDismissed, setDobDismissed] = useState(false);
  const [onboardingStatus, setOnboardingStatus] = useState(null); // null=loading, {should_show, current_step} or false

  useEffect(() => {
    if (!user || user.role !== "user") { setOnboardingStatus(false); return; }
    let cancelled = false;
    api.get("/onboarding/status")
      .then((r) => { if (!cancelled) setOnboardingStatus(r.data || false); })
      .catch(() => { if (!cancelled) setOnboardingStatus(false); });
    return () => { cancelled = true; };
  }, [user?.id, user?.role]);

  if (user === null) {
    return <div className="min-h-screen flex items-center justify-center">Chargement…</div>;
  }
  if (user === false) {
    nav("/connexion", { replace: true });
    return null;
  }

  const planId = user.plan_id || "decouverte";
  // Aligné sur PLANS[].accessible_sections côté backend (server.py)
  const ESSENTIEL_CONFORT_SECTIONS = ["identite", "famille", "couple", "patrimoine_immobilier", "patrimoine_financier", "dettes", "dernieres_volontes", "patrimoine_numerique", "sante", "personnes_confiance"];
  const PATRIMOINE_PLUS_SECTIONS = ["identite", "famille", "couple", "documents_juridiques", "patrimoine_immobilier", "patrimoine_financier", "dettes", "dernieres_volontes", "patrimoine_numerique", "sante", "personnes_confiance"];
  const planAccessible = {
    decouverte: ["identite", "famille"],
    essentiel: ESSENTIEL_CONFORT_SECTIONS,
    confort: ESSENTIEL_CONFORT_SECTIONS,
    patrimoine_plus: PATRIMOINE_PLUS_SECTIONS,
  }[planId] || (user.role === "admin" ? PATRIMOINE_PLUS_SECTIONS : []);

  const isUser = user.role === "user" || user.role === "admin";
  const isPartner = user.role === "partner" || user.role === "admin";
  const isAdmin = user.role === "admin";

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: "var(--auprea-cream)" }}>
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 flex items-center justify-between px-4 py-3" style={{ backgroundColor: "var(--auprea-navy)" }}>
        <LogoLight size={24} />
        <button onClick={() => setOpen(!open)} style={{ color: "var(--auprea-cream)" }} data-testid="mobile-menu-toggle">
          {open ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* Sidebar */}
      <aside
        className={`${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 transition-transform duration-300 fixed lg:sticky top-0 left-0 h-screen w-72 z-30 flex flex-col`}
        style={{ backgroundColor: "var(--auprea-navy)", overflowY: "auto" }}
      >
        <div className="px-6 py-7 border-b" style={{ borderColor: "rgba(202,168,106,0.2)" }}>
          <LogoLight size={28} />
        </div>

        <div className="flex-1 py-4">
          {isUser && (
            <>
              <FoyerBadge />
              <div style={{ padding: "12px 24px", fontSize: 11, letterSpacing: "0.2em", color: "rgba(202,168,106,0.7)", textTransform: "uppercase", fontWeight: 600 }}>
                Mon carnet
              </div>
              {RUBRIQUES.map((r) => {
                const Icon = ICONS[r.icon];
                const locked = !planAccessible.includes(r.id);
                return (
                  <NavLink
                    key={r.id}
                    to={`/carnet/${r.id}`}
                    className={({ isActive }) =>
                      `sidebar-link ${isActive ? "active" : ""} ${locked ? "locked" : ""}`
                    }
                    onClick={(e) => {
                      if (locked) { e.preventDefault(); nav("/tarifs"); }
                      setOpen(false);
                    }}
                    data-testid={`nav-rubrique-${r.id}`}
                    style={{ display: "flex", alignItems: "center", gap: 12 }}
                  >
                    {Icon && <Icon size={18} />}
                    <span style={{ flex: 1 }}>{r.label}</span>
                    {locked && <Lock size={13} style={{ opacity: 0.6 }} />}
                  </NavLink>
                );
              })}

              <div style={{ padding: "20px 24px 12px", fontSize: 11, letterSpacing: "0.2em", color: "rgba(202,168,106,0.7)", textTransform: "uppercase", fontWeight: 600 }}>
                Outils
              </div>
              <NavLink to="/synthese" className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`} onClick={() => setOpen(false)} style={{ display: "flex", alignItems: "center", gap: 12 }} data-testid="nav-synthese">
                <Sparkles size={18} /> Synthèse
              </NavLink>
              <NavLink to="/coffre" className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`} onClick={() => setOpen(false)} style={{ display: "flex", alignItems: "center", gap: 12 }} data-testid="nav-coffre">
                <Archive size={18} /> Coffre-fort
              </NavLink>
              <NavLink to="/partages" className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`} onClick={() => setOpen(false)} style={{ display: "flex", alignItems: "center", gap: 12 }} data-testid="nav-partages">
                <Share2 size={18} /> Partages
              </NavLink>
              <NavLink to="/securite" className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`} onClick={() => setOpen(false)} style={{ display: "flex", alignItems: "center", gap: 12 }} data-testid="nav-securite">
                <Shield size={18} /> Sécurité
              </NavLink>
              <NavLink to="/abonnement" className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`} onClick={() => setOpen(false)} style={{ display: "flex", alignItems: "center", gap: 12 }} data-testid="nav-abonnement">
                <CreditCard size={18} /> Abonnement
              </NavLink>
              <NavLink to="/parrainage" className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`} onClick={() => setOpen(false)} style={{ display: "flex", alignItems: "center", gap: 12 }} data-testid="nav-parrainage">
                <Gift size={18} /> Parrainage
              </NavLink>
              <NavLink to="/annuaire" className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`} onClick={() => setOpen(false)} style={{ display: "flex", alignItems: "center", gap: 12 }} data-testid="nav-annuaire">
                <Briefcase size={18} /> Annuaire pro
              </NavLink>
              <NavLink to="/services" className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`} onClick={() => setOpen(false)} style={{ display: "flex", alignItems: "center", gap: 12 }} data-testid="nav-services">
                <Handshake size={18} /> Services sur mesure
              </NavLink>
            </>
          )}

          {isPartner && (
            <>
              <div style={{ padding: "20px 24px 12px", fontSize: 11, letterSpacing: "0.2em", color: "rgba(202,168,106,0.7)", textTransform: "uppercase", fontWeight: 600 }}>
                Espace partenaire
              </div>
              <NavLink to="/partenaire" className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`} onClick={() => setOpen(false)} style={{ display: "flex", alignItems: "center", gap: 12 }} data-testid="nav-partenaire">
                <UserCheck size={18} /> Carnets partagés
              </NavLink>
            </>
          )}

          {isAdmin && (
            <>
              <div style={{ padding: "20px 24px 12px", fontSize: 11, letterSpacing: "0.2em", color: "rgba(202,168,106,0.7)", textTransform: "uppercase", fontWeight: 600 }}>
                Administration
              </div>
              <NavLink to="/admin" className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`} onClick={() => setOpen(false)} style={{ display: "flex", alignItems: "center", gap: 12 }} data-testid="nav-admin">
                <Shield size={18} /> Tableau de bord
              </NavLink>
            </>
          )}
        </div>

        <div className="border-t p-4" style={{ borderColor: "rgba(202,168,106,0.2)" }}>
          <div style={{ color: "rgba(243,239,230,0.8)", fontSize: 13, marginBottom: 12, paddingLeft: 8 }}>
            <div style={{ fontWeight: 500 }}>{user.full_name}</div>
            <div style={{ fontSize: 12, opacity: 0.7 }}>{user.email}</div>
          </div>
          <button
            onClick={async () => { await logout(); nav("/"); }}
            className="w-full flex items-center justify-center gap-2"
            style={{ color: "var(--auprea-cream)", padding: "10px", border: "1px solid rgba(202,168,106,0.4)", backgroundColor: "transparent", fontSize: 14 }}
            data-testid="btn-logout"
          >
            <LogOut size={16} /> Se déconnecter
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 lg:ml-0 min-w-0" style={{ paddingTop: 60 }}>
        <div className="lg:pt-0 px-4 md:px-8 lg:px-12 py-8 lg:py-12 max-w-6xl mx-auto">
          {isUser && user.role === "user" && !user.date_naissance && (
            <DobReminder onClose={() => setDobDismissed(true)} dismissed={dobDismissed} />
          )}
          {isUser && user.role === "user" && <UpsellBanner />}
          <Outlet />
        </div>
      </main>

      {/* Onboarding "premier remplissage" — modal plein écran, se déclenche automatiquement à la 1re connexion */}
      {onboardingStatus && onboardingStatus.should_show && (
        <OnboardingWizard
          initialStep={onboardingStatus.current_step || 0}
          onFinished={async () => {
            setOnboardingStatus({ ...onboardingStatus, should_show: false });
            try { await refresh && refresh(); } catch (_e) { /* ignore */ }
          }}
        />
      )}
    </div>
  );
}

function DobReminder({ onClose, dismissed }) {
  if (dismissed) return null;
  return (
    <div
      style={{
        background: "rgba(202,168,106,0.12)",
        border: "1px solid rgba(202,168,106,0.4)",
        borderLeft: "3px solid var(--auprea-gold)",
        padding: "14px 18px",
        marginBottom: 24,
        display: "flex",
        alignItems: "center",
        gap: 14,
        flexWrap: "wrap",
      }}
      data-testid="dob-reminder"
    >
      <div style={{ flex: 1, fontSize: 14, lineHeight: 1.55 }}>
        <strong style={{ color: "var(--auprea-navy)" }}>📅 Complétez votre profil</strong> — Votre date de naissance n&apos;est pas encore renseignée.{" "}
        <span style={{ color: "rgba(35,38,46,0.7)" }}>
          Elle nous permet de personnaliser vos alertes successorales.
        </span>
      </div>
      <div className="flex gap-2">
        <NavLink
          to="/carnet/identite"
          className="auprea-btn"
          style={{ padding: "6px 12px", minHeight: "auto", fontSize: 13, textDecoration: "none" }}
          data-testid="dob-reminder-cta"
        >
          Compléter
        </NavLink>
        <button onClick={onClose} style={{ color: "rgba(35,38,46,0.5)", fontSize: 13 }} data-testid="dob-reminder-dismiss">
          Plus tard
        </button>
      </div>
    </div>
  );
}
